import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { StackedBarChart } from "../components/chart/StackedBarChart";
import CustomPaper from "../components/common/customPaper";
import { RatingSpread } from "./RatingSpread";
import { useGetCoverageUniverseQuery } from "../redux/service/modules/dashboard";
import moment from "moment";
import { CHART_COLORS } from "../utils/constant";
import Loader from "../components/common/loader";
import NoDataAvailable from "../components/common/NoDataAvailable";
import { useResponsiveStyles } from "../styles/useResponsiveStyles";

export const transformQuarterlyData = (apiData) => {
    if (!Array.isArray(apiData)) return [];

    return apiData
        .map((quarter) => ({
            name: quarter["current-quater"] || quarter["quater"], // Fallback to "quater" if missing
            largeCapValue: quarter.total_counts?.largecap ?? 0, // Use optional chaining & default value
            midCapValue: quarter.total_counts?.midcap ?? 0,
            smallCapValue: quarter.total_counts?.smallcap ?? 0,
        }))
        .sort((a, b) => {
            // Extract Year & Quarter Number from 'QX-YYYY'
            const [qA, yearA] = a.name.split('-').map((v, i) => (i === 0 ? parseInt(v.substring(1)) : parseInt(v)));
            const [qB, yearB] = b.name.split('-').map((v, i) => (i === 0 ? parseInt(v.substring(1)) : parseInt(v)));

            // Sort by Year, then by Quarter
            return yearA - yearB || qA - qB;
        });
};


export const transformBuySellData = (apiData) => {
    // Get the latest quarter data
    const latestQuarter =
        Array.isArray(apiData) && apiData.length > 0
            ? [...apiData].sort(
                  (a, b) =>
                    {
                        const [qA, yearA] = a["current-quater"].split("-");
                        const [qB, yearB] = b["current-quater"].split("-");
                        
                        // Convert values for proper sorting
                        const quarterA = parseInt(qA.substring(1)); // Extract Q number (Q4 -> 4)
                        const quarterB = parseInt(qB.substring(1)); 
                        const yearNumA = parseInt(yearA);
                        const yearNumB = parseInt(yearB);
          
                        // Sort by Year first, then by Quarter
                        return yearNumB - yearNumA || quarterB - quarterA;
                    }
              )[0]
            : null;

    const buyData = latestQuarter
        ? [
              {
                  name: "BUY",
                  largeCapValue: latestQuarter.buy.largecap,
                  midCapValue: latestQuarter.buy.midcap,
                  smallCapValue: latestQuarter.buy.smallcap,
                  total:
                      latestQuarter.buy.largecap +
                      latestQuarter.buy.midcap +
                      latestQuarter.buy.smallcap,
              },
          ]
        : [];

    const sellData = latestQuarter
        ? [
              {
                  name: "SELL",
                  largeCapValue: latestQuarter.sell.largecap,
                  midCapValue: latestQuarter.sell.midcap,
                  smallCapValue: latestQuarter.sell.smallcap,
                  total:
                      latestQuarter.sell.largecap +
                      latestQuarter.sell.midcap +
                      latestQuarter.sell.smallcap,
              },
          ]
        : [];

    return { buyData, sellData };
};

export const CoverageUniverseChart = () => {
    const [quarterlyData, setQuarterlyData] = useState([]);
    const [buySellData, setBuySellData] = useState([]);
    const { getFontSize, getContainerHeight } = useResponsiveStyles();
    const {
        data: universeData,
        isLoading,
        error,
    } = useGetCoverageUniverseQuery();

    useEffect(() => {
        const { buyData, sellData } = transformBuySellData(universeData);
        setQuarterlyData(transformQuarterlyData(universeData));
        setBuySellData([...buyData, ...sellData]);
    }, [universeData]);

    let staticContent;
    if (isLoading) {
        staticContent = <Loader />;
    } else if (error) {
        staticContent = "Error";
    } else if (
        buySellData?.every((row) =>
            Object.keys(row).every(
                (key) => ["name"].includes(key) || row[key] === 0
            )
        ) &&
        quarterlyData?.every((row) =>
            Object.keys(row).every(
                (key) => ["name"].includes(key) || row[key] === 0
            )
        )
    ) {
        staticContent = <NoDataAvailable />;
    }

    return (
        <CustomPaper sx={{ width: "100%", p: 1 }}>
            <h2
                className="font-bold text-primary mb-3 text-start pt-0.5"
                style={getFontSize("sub-heading")}
            >
                Coverage Universe and Ratings Spread
            </h2>

            <Box>
                <Box
                    sx={{
                        display: "flex",
                        gap: 4,
                        ...getContainerHeight("coverage-universe"),
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flex: 2,
                            flexDirection: "column",
                        }}
                    >
                        {staticContent ? (
                            <div style={styles.staticContent}>
                                {staticContent}
                            </div>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        display: "flex",
                                        height: 300,
                                        marginTop: 5,
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <StackedBarChart
                                            data={quarterlyData}
                                            minWidth={400}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <StackedBarChart
                                            data={buySellData}
                                            minWidth={150}
                                        />
                                    </Box>
                                </Box>
                                <CustomLegend />
                            </>
                        )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <RatingSpread />
                    </Box>
                </Box>
            </Box>
        </CustomPaper>
    );
};

const styles = {
    staticContent: {
        height: "80%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
};

export const CustomLegend = () => {
    const { getFontSize } = useResponsiveStyles();
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <div style={{ display: "flex", justifyContent: "center" }}>
                <div
                    style={{
                        ...getFontSize("sub-heading"),
                        margin: "0 10px",
                        fontWeight: "bold",
                    }}
                >
                    <span
                        style={{
                            color: CHART_COLORS.largeCap,
                            ...getFontSize("heading"),
                            marginRight: "5px",
                        }}
                    >
                        ●
                    </span>
                    Large Cap
                </div>
                <div
                    style={{
                        margin: "0 10px",
                        ...getFontSize("sub-heading"),
                        fontWeight: "bold",
                    }}
                >
                    <span
                        style={{
                            color: CHART_COLORS.midCap,
                            ...getFontSize("heading"),
                            marginRight: "5px",
                        }}
                    >
                        ●
                    </span>
                    Mid Cap
                </div>
                <div
                    style={{
                        margin: "0 10px",
                        ...getFontSize("sub-heading"),
                        fontWeight: "bold",
                    }}
                >
                    <span
                        style={{
                            color: CHART_COLORS.smallCap,
                            ...getFontSize("heading"),
                            marginRight: "5px",
                        }}
                    >
                        ●
                    </span>
                    Small Cap
                </div>
            </div>
        </Box>
    );
};
